<script setup lang="ts">
import {defineAsyncComponent, onMounted, onUnmounted, ref, nextTick} from 'vue';
import {getSettings, getTranslation} from '@/ts/utilities.ts'
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';

const RenderAction = defineAsyncComponent(() => import('@/components/helpers/RenderAction.vue'))
const ShowModal = defineAsyncComponent(() => import('@/components/ShowModal.vue'))

const settings = getSettings()

const usps = ref([])
const ulRef = ref(null)
const hasOverflow = ref(false)
let observer: MutationObserver | null = null

const getUsps = async () => {
	usps.value = settings.pageTopUsps
	await nextTick()
	checkOverflow()
}

const checkOverflow = () => {
	if (ulRef.value) {
		hasOverflow.value = ulRef.value.scrollWidth > ulRef.value.clientWidth
	}
}

onMounted(() => {
	getUsps()
	window.addEventListener('resize', checkOverflow)

	// MutationObserver: tjek når ul får børn/ændres
	if (ulRef.value) {
		observer = new MutationObserver(() => {
			checkOverflow()
		})
		observer.observe(ulRef.value, {
			childList: true,
			subtree: true,
			attributes: true
		})
	}
})

onUnmounted(() => {
	window.removeEventListener('resize', checkOverflow)
	if (observer) {
		observer.disconnect()
	}
})
</script>

<template>
	<div v-if="settings.enableTopBarHeader"
	     class="container-fluid">
		<div class="bg-beige py-2 lg:flex">
			<div class="container flex justify-between overflow-hidden">
				<div class="relative flex-1 min-w-0 lg:flex-1">
					<ul ref="ulRef" v-if="usps"
					    class="flex gap-8 list-none overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbar-hide pr-24 lg:pr-0 lg:overflow-visible lg:whitespace-normal lg:snap-none">
						<li
							v-for="(usp, index) in usps"
							:key="index"
							class="flex items-center gap-x-2 shrink-0 snap-start lg:shrink">
							<ShowModal
								v-if="usp.showContentInModal"
								:modal-title="usp.header"
								:modal-content="usp.contentText">
								<font-awesome-icon :icon="['fal', usp.icon]"/>
								<small>{{ usp.header }}</small>
							</ShowModal>

							<RenderAction
								v-else-if="usp.link"
								theme="link"
								size="none"
								:link="usp.link">
								<font-awesome-icon
									v-if="usp.icon"
									:icon="['fal', usp.icon]"
								/>
								<small>{{ usp.header }}</small>
							</RenderAction>

							<template v-else>
								<font-awesome-icon
									v-if="usp.icon"
									:icon="['fal', usp.icon]"
								/>
								<small>{{ usp.header }}</small>
							</template>
						</li>
					</ul>
					<!-- Scroll indicator gradient with chevron (mobil kun) -->
					<div v-if="hasOverflow" class="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-beige to-transparent pointer-events-none lg:hidden flex items-center justify-end pr-0">
						<font-awesome-icon :icon="['fal', 'chevron-right']" class="text-black text-lg opacity-60"/>
					</div>
				</div>
				<ul class="flex gap-4 hidden lg:flex list-none">
					<li v-if="settings?.contactUsPage">
						<RenderAction
							action="link"
							theme="link"
							size="none"
							:data="settings?.contactUsPage">
							<small>{{ getTranslation('Header.ContactUs') }}</small>
						</RenderAction>
					</li>
					<li class="flex items-center gap-x-2">
						<small>{{ getTranslation('E-Mærket') }}</small>
						<font-awesome-icon :icon="['fak', 'emaerket']"/>
					</li>
				</ul>
			</div>
		</div>
	</div>
</template>
